package rendering

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.unit.dp

private val COLOR_TRACK = Color(0xFF111122)
private val COLOR_AMBER = Color(0xFFFFAB40)
private val COLOR_RED = Color(0xFFFF1744)
private const val MAX_DISPLAY_LINES = 20
private const val SEGMENT_GAP = 2f

/**
 * Vertical bar showing pending garbage lines.
 *
 * Draws [pendingLines] filled segments from the bottom up, with color
 * interpolating from amber (low) to red (high). The track background
 * always shows [MAX_DISPLAY_LINES] slots.
 *
 * @param pendingLines Number of pending garbage lines (0..∞, capped at [MAX_DISPLAY_LINES] for display).
 * @param modifier     Applied to the [Canvas] composable.
 */
@Composable
fun GarbagePendingBar(
    pendingLines: Int,
    modifier: Modifier = Modifier,
) {
    Canvas(
        modifier = modifier
            .width(12.dp)
            .fillMaxHeight(),
    ) {
        val totalSlots = MAX_DISPLAY_LINES
        val filledSlots = pendingLines.coerceIn(0, totalSlots)
        val segmentHeight = (size.height - SEGMENT_GAP * (totalSlots - 1)) / totalSlots

        // Draw track (all slots as background)
        for (i in 0 until totalSlots) {
            val y = size.height - (i + 1) * segmentHeight - i * SEGMENT_GAP
            drawRect(
                color = COLOR_TRACK,
                topLeft = Offset(0f, y),
                size = Size(size.width, segmentHeight),
            )
        }

        // Draw filled segments
        for (i in 0 until filledSlots) {
            val t = if (totalSlots > 1) i.toFloat() / (totalSlots - 1) else 0f
            val color = lerpColor(COLOR_AMBER, COLOR_RED, t)
            val y = size.height - (i + 1) * segmentHeight - i * SEGMENT_GAP
            drawRect(
                color = color,
                topLeft = Offset(0f, y),
                size = Size(size.width, segmentHeight),
            )
        }
    }
}

/**
 * Linearly interpolates between two [Color]s.
 *
 * @param from  Start color (t = 0).
 * @param to    End color (t = 1).
 * @param t     Interpolation factor in [0, 1].
 */
private fun lerpColor(from: Color, to: Color, t: Float): Color {
    val clampedT = t.coerceIn(0f, 1f)
    return Color(
        red = from.red + (to.red - from.red) * clampedT,
        green = from.green + (to.green - from.green) * clampedT,
        blue = from.blue + (to.blue - from.blue) * clampedT,
        alpha = 1f,
    )
}
